import { useState, useCallback } from 'react';
import { GoogleAdsService } from '../services/googleAds';
import { AdsCampaign, AdSet } from '../types/ads';
import { useAdsStore } from '../store/adsStore';

export const useGoogleAds = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const adsService = GoogleAdsService.getInstance();
  const { updateCampaign } = useAdsStore();

  const createCampaign = useCallback(async (campaign: AdsCampaign) => {
    setIsLoading(true);
    setError(null);
    try {
      const googleCampaignId = await adsService.createCampaign(campaign);
      
      // Create ad sets for each selected clinician
      const adSets: AdSet[] = await Promise.all(
        campaign.selectedClinicians.map(async (clinicianId) => {
          const clinician = { id: clinicianId, name: 'Test', specialties: ['Anxiety'] }; // In production, get from store
          const adCopy = await adsService.generateAdCopy(clinician);
          
          return {
            id: crypto.randomUUID(),
            campaignId: campaign.id,
            clinicianId,
            headlines: adCopy.headlines,
            descriptions: adCopy.descriptions,
            finalUrl: `/providers/${clinicianId}`,
            performance: {
              impressions: 0,
              clicks: 0,
              conversions: 0,
              spend: 0,
            },
          };
        })
      );

      await adsService.createAdSets(googleCampaignId, adSets);
      
      // Update local campaign withAdvertising Partners ID
      updateCampaign(campaign.id, { googleCampaignId });
      
      return googleCampaignId;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [adsService, updateCampaign]);

  const toggleCampaignStatus = useCallback(async (
    campaignId: string,
    status: 'active' | 'paused'
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      if (status === 'active') {
        await adsService.resumeCampaign(campaignId);
      } else {
        await adsService.pauseCampaign(campaignId);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [adsService]);

  const updateCampaignPerformance = useCallback(async (campaignId: string) => {
    try {
      const performance = await adsService.getCampaignPerformance(campaignId);
      updateCampaign(campaignId, { performance });
    } catch (err) {
      console.error('Failed to update campaign performance:', err);
    }
  }, [adsService, updateCampaign]);

  return {
    isLoading,
    error,
    createCampaign,
    toggleCampaignStatus,
    updateCampaignPerformance,
  };
};